import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from './ui/Button'
import { api, extractApiError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

/**
 * Company-wide "remind me before a task is due" editor. A Company Admin
 * (or Sub Admin) picks one or more lead times; the due-date reminder
 * runner sends an in-app + email reminder to a task's assignees as each
 * lead time is reached. Empty selection = task reminders off.
 *
 * Rides the SAME `PATCH /admin/company` endpoint as the company timezone
 * (field: `taskReminderOffsets`, minutes before due) and reads the
 * current value from the auth-store tenant, so the two company settings
 * stay consistent. Mirrors `CompanyTimezone.jsx`.
 */

// Default ladder — matches the backend `DEFAULT_TASK_REMINDER_OFFSETS`
// so an unset tenant renders the same selection the runner would use.
const DEFAULT_OFFSETS = [30, 1440]

// Preset lead times offered as chips (minutes before due). "30 Minutes
// Before" is the headline option from the requirement.
const PRESETS = [30, 60, 120, 240, 1440, 2880, 10080]

// Default overdue cadence — matches the backend
// `DEFAULT_OVERDUE_REMINDER_INTERVAL` (1 day) so an unset tenant renders
// the same repeat the runner would use.
const DEFAULT_OVERDUE_INTERVAL = 1440

// Preset overdue cadences offered as single-select chips. `0` = off (a
// single overdue notice, no repeat). Every positive value is at least
// the backend's 60-minute floor, so the picker can never set an unsafe
// cadence.
const OVERDUE_PRESETS = [0, 60, 360, 720, 1440, 2880, 10080]

/** Label for an overdue cadence — "Off" for 0, otherwise "Every <n>". */
function formatOverdueInterval(minutes) {
  if (!minutes) return 'Off'
  return `Every ${formatLeadTime(minutes)}`
}

/** Human label for a lead time in minutes — "30 minutes", "2 hours",
 *  "1 day", "1 week" — the coarsest whole unit. */
export function formatLeadTime(minutes) {
  if (minutes % 10080 === 0) {
    const w = minutes / 10080
    return `${w} week${w === 1 ? '' : 's'}`
  }
  if (minutes % 1440 === 0) {
    const d = minutes / 1440
    return `${d} day${d === 1 ? '' : 's'}`
  }
  if (minutes % 60 === 0) {
    const h = minutes / 60
    return `${h} hour${h === 1 ? '' : 's'}`
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

const sameSet = (a, b) =>
  a.length === b.length && a.every((v, i) => v === b[i])

export function CompanyTaskRemindersEditor({ onSaved }) {
  // Undefined (older API) → default ladder; an explicit [] stays off.
  const stored = useAuthStore((s) => s.tenant?.taskReminderOffsets)
  const current = useMemo(
    () =>
      (Array.isArray(stored) ? stored : DEFAULT_OFFSETS)
        .slice()
        .sort((a, b) => a - b),
    [stored],
  )
  const [draft, setDraft] = useState(current)

  // Re-sync when the store changes (e.g. right after a save's refreshUser()).
  useEffect(() => {
    setDraft(current)
  }, [current])

  // Overdue repeat cadence (minutes). Undefined (older API) → default;
  // `0` = off (single overdue notice).
  const storedOverdue = useAuthStore(
    (s) => s.tenant?.overdueReminderIntervalMinutes,
  )
  const currentOverdue = useMemo(
    () =>
      Number.isFinite(storedOverdue) ? storedOverdue : DEFAULT_OVERDUE_INTERVAL,
    [storedOverdue],
  )
  const [overdueDraft, setOverdueDraft] = useState(currentOverdue)
  useEffect(() => {
    setOverdueDraft(currentOverdue)
  }, [currentOverdue])

  // Chips = presets ∪ any custom stored value (so an offset set via the API
  // that isn't a preset still shows and can be toggled off, never silently
  // dropped on the next save).
  const chips = useMemo(
    () => [...new Set([...PRESETS, ...current])].sort((a, b) => a - b),
    [current],
  )

  // Overdue chips = presets ∪ the stored value, so a custom cadence set via
  // the API still shows and stays selectable.
  const overdueChips = useMemo(
    () =>
      [...new Set([...OVERDUE_PRESETS, currentOverdue])].sort((a, b) => a - b),
    [currentOverdue],
  )

  const mutation = useMutation({
    mutationFn: async ({ offsets, overdue }) => {
      const res = await api.patch('/admin/company', {
        taskReminderOffsets: offsets,
        overdueReminderIntervalMinutes: overdue,
      })
      return res.data
    },
    onSuccess: async () => {
      toast.success('Task reminders saved successfully.')
      await useAuthStore.getState().refreshUser()
      onSaved?.()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const unchanged =
    sameSet(draft, current) && overdueDraft === currentOverdue

  function toggle(minutes) {
    setDraft((prev) =>
      prev.includes(minutes)
        ? prev.filter((m) => m !== minutes)
        : [...prev, minutes].sort((a, b) => a - b),
    )
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-[color:var(--color-ink)]">
          Before a task is due
        </p>
        <div className="flex flex-wrap gap-2">
          {chips.map((m) => {
            const on = draft.includes(m)
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggle(m)}
                aria-pressed={on}
                className={
                  'px-3 py-1.5 rounded-full text-sm border transition-colors cursor-pointer ' +
                  (on
                    ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium'
                    : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] hover:border-[color:var(--color-accent)]')
                }
              >
                {formatLeadTime(m)} before
              </button>
            )
          })}
        </div>
      </div>

      {/* Overdue repeat — single-select cadence for how often the "task is
       *  overdue" reminder resends until the task is completed. "Off" sends
       *  a single overdue notice (the legacy behaviour). */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-[color:var(--color-ink)]">
          While a task is overdue
        </p>
        <div className="flex flex-wrap gap-2">
          {overdueChips.map((m) => {
            const on = overdueDraft === m
            return (
              <button
                key={m}
                type="button"
                onClick={() => setOverdueDraft(m)}
                aria-pressed={on}
                className={
                  'px-3 py-1.5 rounded-full text-sm border transition-colors cursor-pointer ' +
                  (on
                    ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium'
                    : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] hover:border-[color:var(--color-accent)]')
                }
              >
                {formatOverdueInterval(m)}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => mutation.mutate({ offsets: draft, overdue: overdueDraft })}
          loading={mutation.isPending}
          disabled={mutation.isPending || unchanged}
        >
          Save Reminders
        </Button>
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          {draft.length === 0
            ? 'No pre-due reminders. '
            : `Pre-due: ${draft.map(formatLeadTime).join(', ')} before due. `}
          {overdueDraft === 0
            ? 'Overdue reminder sent once.'
            : `Overdue reminder repeats ${formatOverdueInterval(overdueDraft).toLowerCase()} until completed.`}
        </span>
      </div>
      {/* Email delivery is gated SEPARATELY from these lead times. The runner
       *  always rings the in-app bell for a task's assignees, but a reminder
       *  EMAIL only goes out when the platform-level "Task Due Soon" / "Task
       *  Overdue" email templates are active (Super Admin → Email) — a Company
       *  Admin can't toggle those from here. Without this note the "I set
       *  reminders but no email arrived" gap is invisible on this screen (QA):
       *  it surfaces the dependency and points at where the reason is logged
       *  (Email Status Logs), rather than promising a fix this screen can't do. */}
      <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs text-[color:var(--color-ink-muted)] leading-relaxed">
        Reminders go to a task's assignees and always show in the in-app
        notification bell. A reminder{' '}
        <span className="font-medium text-[color:var(--color-ink)]">email</span>{' '}
        is also sent only when the platform ‘Task Due Soon’ and ‘Task Overdue’
        email templates are active. If reminder emails aren't arriving, check
        Email Status Logs for the reason, or ask your platform administrator to
        enable them.
      </div>
    </div>
  )
}
